import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader, Modal, Input } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function Sequences() {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', steps: [{ subject: '', body: '', delayDays: 0 }] });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSequences(); }, []);

  const fetchSequences = async () => {
    try { const r = await api.get('/sequences'); setSequences(r.data.data || []); }
    catch {} finally { setLoading(false); }
  };

  const addStep = () => setForm(p => ({ ...p, steps: [...p.steps, { subject: '', body: '', delayDays: p.steps.length }] }));
  const updateStep = (i, k, v) => setForm(p => { const s = [...p.steps]; s[i] = { ...s[i], [k]: v }; return { ...p, steps: s }; });
  const removeStep = (i) => setForm(p => ({ ...p, steps: p.steps.filter((_, idx) => idx !== i) }));

  const createSequence = async () => {
    if (!form.name) return toast.error('Sequence name required');
    setSaving(true);
    try { await api.post('/sequences', form); toast.success('Sequence created!'); setShowModal(false); fetchSequences(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Sequences" subtitle="Automated follow-up email sequences" action={<Button onClick={() => setShowModal(true)}>+ New Sequence</Button>} />

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        sequences.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔄</div>
            <h3 style={{ color: '#ccc', marginBottom: 8 }}>No sequences yet</h3>
            <p style={{ color: '#555', fontSize: 14, marginBottom: 24 }}>Create automated follow-up sequences with multiple steps and delays.</p>
            <Button onClick={() => setShowModal(true)}>+ Create Sequence</Button>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sequences.map(s => (
              <Card key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: '#555' }}>{Array.isArray(s.steps) ? s.steps.length : 0} steps</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant="ghost">Edit</Button>
                  <Button size="sm" variant="secondary">Assign Leads</Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Create Sequence" size="lg">
        <Input label="Sequence Name *" placeholder="e.g. Cold Outreach Sequence" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: '#ccc' }}>Steps ({form.steps.length})</label>
            <Button size="sm" variant="outline" onClick={addStep}>+ Add Step</Button>
          </div>
          {form.steps.map((step, i) => (
            <div key={i} style={{ background: '#1a1a1a', borderRadius: 12, padding: 16, marginBottom: 10, border: '1px solid rgba(46,204,113,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#2ECC71' }}>Step {i + 1}</span>
                {i > 0 && <button onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', color: '#E74C3C', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>}
              </div>
              <Input label="Subject" placeholder="Email subject..." value={step.subject} onChange={e => updateStep(i, 'subject', e.target.value)} />
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 5 }}>Body</label>
                <textarea value={step.body} onChange={e => updateStep(i, 'body', e.target.value)} rows={3} placeholder="Email body..." style={{ width: '100%', padding: '9px 12px', background: '#111', border: '1px solid rgba(46,204,113,0.12)', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <Input label={`Send after (days)${i === 0 ? ' — 0 = immediately' : ''}`} type="number" value={step.delayDays} onChange={e => updateStep(i, 'delayDays', parseInt(e.target.value) || 0)} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Cancel</Button>
          <Button loading={saving} onClick={createSequence} style={{ flex: 2 }}>Create Sequence</Button>
        </div>
      </Modal>
    </div>
  );
}
